// ── Inline Learn Card for Chat ──────────────────────────────
// Renders comprehension loop progress/probes directly in the chat stream.
// Follows the RefineCard.js pattern.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { authFetch } from '../api';
import TelegramLinkModal from '../TelegramLinkModal';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Inline Audio Player ─────────────────────────────────────
function AudioLessonPlayer({ conceptId, nodes, topic, paperTitle, cachedAudio, onCacheAudio }) {
  const [status, setStatus] = useState(cachedAudio ? 'ready' : 'idle'); // idle | loading | ready | playing | paused | error
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1.0);
  const audioRef = useRef(null);
  const objectUrlRef = useRef(null);

  // Clean up object URL on unmount
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  // If we have cached audio, create the audio element
  useEffect(() => {
    if (cachedAudio && !audioRef.current) {
      const blob = base64ToBlob(cachedAudio.audioBase64, cachedAudio.mimeType || 'audio/mpeg');
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      const audio = new Audio(url);
      audio.playbackRate = speed;
      audioRef.current = audio;
      setStatus('ready');
      audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
      audio.addEventListener('timeupdate', () => setProgress(audio.currentTime));
      audio.addEventListener('ended', () => setStatus('ready'));
    }
  }, [cachedAudio, speed]);

  const generateAudio = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const res = await authFetch(`${API_URL}/api/learn/teach-audio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nodes: (nodes || []).map(n => ({ id: n.id, type: n.data?.type || n.type, label: n.data?.label || n.label, reasoning: n.data?.reasoning || n.reasoning, parentIds: n.data?.parentIds || [], difficulty: n.data?.difficulty, paperRef: n.data?.paperRef })),
          topic, conceptId, ...(paperTitle ? { paperTitle } : {}),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Audio generation failed' }));
        throw new Error(err.error || 'Audio generation failed');
      }
      const data = await res.json();
      // Cache the audio data
      onCacheAudio?.(conceptId, data);
      // Create audio element
      const blob = base64ToBlob(data.audioBase64, data.mimeType || 'audio/mpeg');
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      const audio = new Audio(url);
      audio.playbackRate = speed;
      audioRef.current = audio;
      audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
      audio.addEventListener('timeupdate', () => setProgress(audio.currentTime));
      audio.addEventListener('ended', () => setStatus('ready'));
      setStatus('ready');
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  }, [conceptId, nodes, topic, paperTitle, onCacheAudio, speed]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (status === 'playing') {
      audio.pause();
      setStatus('paused');
    } else {
      audio.play();
      setStatus('playing');
    }
  }, [status]);

  const seek = useCallback((e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audio.currentTime = pct * duration;
  }, [duration]);

  const skip = useCallback((seconds) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(audio.duration, audio.currentTime + seconds));
  }, []);

  const cycleSpeed = useCallback(() => {
    const speeds = [0.75, 1.0, 1.25, 1.5, 2.0];
    const idx = speeds.indexOf(speed);
    const next = speeds[(idx + 1) % speeds.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  }, [speed]);

  const formatTime = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (status === 'idle') {
    return (
      <button className="learn-audio-btn" onClick={generateAudio}>
        🎧 Listen to Audio Lesson
      </button>
    );
  }

  if (status === 'loading') {
    return (
      <div className="learn-audio-loading">
        <span className="learn-pulse">●</span> Generating audio lesson...
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="learn-audio-error">
        <span>Audio failed: {error}</span>
        <button className="learn-audio-btn learn-audio-btn-sm" onClick={generateAudio}>Retry</button>
      </div>
    );
  }

  // ready | playing | paused
  return (
    <div className="learn-audio-player">
      <div className="learn-audio-controls">
        <button className="learn-audio-skip" onClick={() => skip(-15)} title="Back 15s">⏪</button>
        <button className="learn-audio-play" onClick={togglePlay}>
          {status === 'playing' ? '⏸' : '▶'}
        </button>
        <button className="learn-audio-skip" onClick={() => skip(15)} title="Forward 15s">⏩</button>
      </div>
      <div className="learn-audio-progress-wrap" onClick={seek}>
        <div className="learn-audio-progress-bar">
          <div
            className="learn-audio-progress-fill"
            style={{ width: duration ? `${(progress / duration) * 100}%` : '0%' }}
          />
        </div>
      </div>
      <div className="learn-audio-time">
        {formatTime(progress)} / {formatTime(duration)}
      </div>
      <button className="learn-audio-speed" onClick={cycleSpeed} title="Playback speed">
        {speed}×
      </button>
    </div>
  );
}

function base64ToBlob(base64, mimeType) {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mimeType });
}

// ── Local TTS button (Kokoro-82M) ─────────────────────────────
// One-shot synthesis of the current lesson body via POST /api/voice/tts-local.
// Streams WAV bytes back, plays via <audio>. Click again to stop.
// Lives inside LearnCard's teaching state — fires on the active lesson,
// independent of cachedLesson presence (works even on first-time teach).
function LocalLessonTTS({ conceptLabel, teachContent }) {
  const [ttsState, setTtsState] = useState('idle'); // idle | loading | playing | error
  const [ttsError, setTtsError] = useState(null);
  const audioRef = useRef(null);
  // Telegram push state: idle | sending | ok | err
  const [tgState, setTgState] = useState('idle');
  const [tgMsg, setTgMsg] = useState(null);
  const [tgModalOpen, setTgModalOpen] = useState(false);

  const buildText = () => {
    const parts = [];
    if (conceptLabel) parts.push(conceptLabel);
    if (teachContent?.explanation) parts.push(teachContent.explanation);
    if (teachContent?.example) parts.push(`Example: ${teachContent.example}`);
    if (teachContent?.analogy) parts.push(`Think of it like: ${teachContent.analogy}`);
    if (teachContent?.keyTakeaways?.length) {
      parts.push(`Key takeaways: ${teachContent.keyTakeaways.join('. ')}`);
    }
    return parts.join('. ').slice(0, 8000);
  };

  const handleClick = async () => {
    if (ttsState === 'playing' && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setTtsState('idle');
      return;
    }
    setTtsState('loading');
    setTtsError(null);
    try {
      const res = await authFetch(`${API_URL}/api/voice/tts-local`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: buildText(), voice: 'af_heart', speed: 1.0 }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setTtsState('idle'); URL.revokeObjectURL(url); };
      audio.onerror = () => { setTtsState('error'); setTtsError('Playback failed'); };
      setTtsState('playing');
      await audio.play();
    } catch (err) {
      setTtsState('error');
      setTtsError(err.message);
    }
  };

  useEffect(() => () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  }, []);

  // Push the same composed lesson text to the user's linked Telegram chat.
  // Server synthesises WAV via Kokoro + Telegram sendAudio. Same gating
  // pattern as Overview push: if not linked, server returns 400 and we
  // surface a hint to link the bot first.
  const doSendToTelegram = async () => {
    setTgState('sending');
    setTgMsg(null);
    try {
      const res = await authFetch(`${API_URL}/api/integrations/telegram/send-lesson-audio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conceptLabel, text: buildText() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (/not linked/i.test(data.error || '')) {
          setTgState('idle');
          setTgMsg(null);
          setTgModalOpen(true);
          return;
        }
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setTgState('ok');
      setTgMsg(`✓ Sent to Telegram (${data.duration}s)`);
      setTimeout(() => { setTgState('idle'); setTgMsg(null); }, 4000);
    } catch (err) {
      setTgState('err');
      setTgMsg(`✗ ${err.message}`);
      setTimeout(() => { setTgState('idle'); setTgMsg(null); }, 6000);
    }
  };
  const handleSendToTelegram = async () => {
    if (tgState === 'sending') return;
    await doSendToTelegram();
  };

  const label = ttsState === 'loading' ? '⟳ Synthesizing lesson…'
    : ttsState === 'playing' ? '⏹ Stop'
    : ttsState === 'error' ? `⚠ Retry (${ttsError?.slice(0, 40) || 'failed'})`
    : '🔊 Listen to this Lesson';
  const color = ttsState === 'playing' ? '#34d399' : ttsState === 'error' ? '#f87171' : '#38bdf8';
  const tgLabel = tgState === 'sending' ? '⟳ Sending…' : '📤 Push to Telegram';
  const tgColor = tgState === 'err' ? '#f87171' : tgState === 'ok' ? '#34d399' : '#a78bfa';
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
      <button
        type="button"
        onClick={handleClick}
        disabled={ttsState === 'loading'}
        aria-label={ttsState === 'playing' ? 'Stop lesson audio' : 'Listen to this lesson'}
        title={ttsState === 'error' ? `TTS failed: ${ttsError}` : 'Local TTS via Kokoro-82M'}
        style={{
          background: ttsState === 'playing' ? 'rgba(52,211,153,0.18)' : ttsState === 'error' ? 'rgba(248,113,113,0.18)' : 'rgba(56,189,248,0.15)',
          color,
          border: `1px solid ${color}55`,
          padding: '6px 12px',
          fontSize: 12,
          fontFamily: 'var(--font-mono)',
          fontWeight: 600,
          borderRadius: 6,
          cursor: ttsState === 'loading' ? 'wait' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          transition: 'background 0.15s ease',
        }}
      >
        {label}
      </button>
      <button
        type="button"
        onClick={handleSendToTelegram}
        disabled={tgState === 'sending'}
        aria-label="Push this lesson to Telegram as audio"
        title="Send lesson audio to your linked Telegram chat"
        style={{
          background: tgState === 'err' ? 'rgba(248,113,113,0.18)' : tgState === 'ok' ? 'rgba(52,211,153,0.18)' : 'rgba(167,139,250,0.15)',
          color: tgColor,
          border: `1px solid ${tgColor}55`,
          padding: '6px 12px',
          fontSize: 12,
          fontFamily: 'var(--font-mono)',
          fontWeight: 600,
          borderRadius: 6,
          cursor: tgState === 'sending' ? 'wait' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {tgLabel}
      </button>
      {tgMsg && (
        <span
          role="status"
          aria-live="polite"
          style={{ fontSize: 11, color: tgState === 'err' ? '#f87171' : '#34d399', fontFamily: 'var(--font-mono)' }}
        >
          {tgMsg}
        </span>
      )}
      {/* Inline link modal — opens on "not linked" error, auto-retries push on success. */}
      {tgModalOpen && (
        <TelegramLinkModal
          onClose={() => setTgModalOpen(false)}
          onLinked={() => { setTgModalOpen(false); doSendToTelegram(); }}
        />
      )}
    </div>
  );
}

function MasteryBadge({ mastery }) {
  const color = mastery >= 8 ? '#22c55e' : mastery >= 5 ? '#fbbf24' : mastery >= 1 ? '#ef4444' : '#6b7280';
  const label = mastery >= 8 ? 'MASTERED' : mastery >= 5 ? 'PARTIAL' : mastery >= 1 ? 'GAP' : 'NEW';
  return (
    <span className="learn-mastery-badge" style={{ background: `${color}18`, color, borderColor: `${color}40` }}>
      {mastery}/10 {label}
    </span>
  );
}

// ── Multiple-choice probe ─────────────────────────────────────
// Single-select: tap = lock + submit immediately (game feel).
// Multi-select: tap toggles selection, explicit "Submit" button.
function McqProbe({ probe, conceptId, cachedLesson, showingReview, onSubmit, onSkip, onReviewLesson }) {
  const [selected, setSelected] = useState([]);
  const [locked, setLocked] = useState(false);
  const isMulti = !!probe.isMultiSelect;
  const options = probe.options || [];

  const toggle = (optId) => {
    if (locked) return;
    if (isMulti) {
      setSelected(prev => prev.includes(optId) ? prev.filter(id => id !== optId) : [...prev, optId]);
    } else {
      // Single-select: lock + submit immediately
      setSelected([optId]);
      setLocked(true);
      onSubmit([optId]);
    }
  };

  const submitMulti = () => {
    if (locked || selected.length === 0) return;
    setLocked(true);
    onSubmit(selected);
  };

  return (
    <div className="learn-card-probe learn-mcq">
      <div className="learn-probe-question">
        <span className="learn-probe-type">
          {probe.probeType?.toUpperCase() || 'QUIZ'}{isMulti ? ' · MULTI-SELECT' : ''}
        </span>
        {probe.question}
      </div>

      <div className="learn-mcq-options" role={isMulti ? 'group' : 'radiogroup'} aria-label="Answer options">
        {options.map((opt, idx) => {
          const isSelected = selected.includes(opt.id);
          const letter = String.fromCharCode(65 + idx); // A, B, C, D
          return (
            <button
              key={opt.id}
              type="button"
              role={isMulti ? 'checkbox' : 'radio'}
              aria-checked={isSelected}
              disabled={locked}
              className={`learn-mcq-option${isSelected ? ' selected' : ''}${locked ? ' locked' : ''}`}
              onClick={() => toggle(opt.id)}
            >
              <span className="learn-mcq-letter">{letter}</span>
              <span className="learn-mcq-text">{opt.text}</span>
            </button>
          );
        })}
      </div>

      <div className="learn-card-actions">
        {isMulti && !locked && (
          <button
            type="button"
            className="learn-card-btn learn-card-btn-primary"
            disabled={selected.length === 0}
            onClick={submitMulti}
          >
            Lock In Answer
          </button>
        )}
        {!locked && (
          <button type="button" className="learn-card-btn learn-card-btn-skip" onClick={onSkip}>
            Skip
          </button>
        )}
        {cachedLesson && !locked && (
          <button type="button" className="learn-card-btn" onClick={onReviewLesson}>
            {showingReview ? 'Hide Lesson' : '📖 Review Lesson'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function LearnCard({ state, onAction, mnemonicJob, onGenerateVideo, onPlayVideo, canDrill, nodes, topic, paperTitle, audioCache, onCacheAudio }) {
  const [answer, setAnswer] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [hintIndex, setHintIndex] = useState(0);

  if (!state) return null;

  const { status, conceptId, conceptLabel, conceptReasoning, mastery,
    probe, feedback, correctAnswer, correct, misconceptions, nextAction,
    teachContent, adaptSummary, cachedLesson, _showingReview,
    milestoneId: _milestoneId, milestoneLabel, challenge, coveredConcepts, followUpQuestions: _followUpQuestions, // eslint-disable-line no-unused-vars
    detail, error, masteryMap: finalMasteryMap, totalConcepts, showHint } = state;

  const isActive = status === 'generating_probe' || status === 'evaluating' || status === 'adapting';
  const isDone = status === 'done' || status === 'complete';
  const isProbing = status === 'probing';
  const isFeedback = status === 'feedback' || status === 'milestone_feedback';
  const isSocratic = status === 'socratic' && challenge;
  const isTeaching = status === 'teaching';
  const isAdaptSummary = status === 'adapt_summary';
  const isError = status === 'error';

  return (
    <div className={`chat-learn-card ${isActive ? 'learn-card-active' : ''} ${isDone ? 'learn-card-done' : ''} ${isError ? 'learn-card-error' : ''}`}>
      {/* Header */}
      <div className="learn-card-header">
        <span className="learn-card-icon">
          {(isActive || (isTeaching && !teachContent)) && <span className="learn-pulse">●</span>}
          {isTeaching && teachContent && '📖'}
          {isProbing && '?'}
          {isSocratic && '?'}
          {isFeedback && (correct ? '✓' : '✗')}
          {isAdaptSummary && '💡'}
          {isDone && '★'}
          {isError && '✗'}
        </span>
        <span className="learn-card-title">
          {isTeaching && !teachContent && `Preparing lesson for "${conceptLabel}"`}
          {isTeaching && teachContent && `Lesson: ${conceptLabel}`}
          {status === 'generating_probe' && (conceptLabel ? `Preparing question for "${conceptLabel}"` : 'Preparing next question...')}
          {isProbing && `Quiz: ${conceptLabel}`}
          {status === 'evaluating' && 'Evaluating your answer...'}
          {isFeedback && `Feedback: ${conceptLabel}`}
          {status === 'adapting' && 'Generating alternative explanation...'}
          {isAdaptSummary && `Alternative Explanation: ${conceptLabel}`}
          {isSocratic && `Milestone: ${milestoneLabel}`}
          {isDone && `Learning Complete${totalConcepts ? ` — ${totalConcepts} concepts` : ''}`}
          {isError && 'Learning Error'}
        </span>
        {(isActive || isProbing || isSocratic || (isTeaching && !teachContent)) && (
          <button className="learn-card-stop" onClick={() => onAction?.({ actionType: 'stopLearn' })}>Stop</button>
        )}
      </div>

      {/* Active spinner states */}
      {(isActive || (isTeaching && !teachContent)) && (
        <div className="learn-card-status">
          {detail || 'Processing...'}
        </div>
      )}

      {/* Teaching lesson */}
      {isTeaching && teachContent && (
        <div className="learn-card-lesson">
          {/* Local TTS (Kokoro-82M) — synthesises lesson body on demand.
              Lives at the top of the lesson card so it's the first thing
              the user sees. Stateless: re-renders the button per click. */}
          <LocalLessonTTS
            conceptLabel={conceptLabel}
            teachContent={teachContent}
          />
          <div className="learn-lesson-explanation">{teachContent.explanation}</div>

          {teachContent.keyTakeaways?.length > 0 && (
            <div className="learn-lesson-takeaways">
              <strong>Key Takeaways:</strong>
              <ul>{teachContent.keyTakeaways.map((t, i) => <li key={i}>{t}</li>)}</ul>
            </div>
          )}

          {teachContent.example && (
            <div className="learn-lesson-example">
              <strong>Example:</strong>
              <div>{teachContent.example}</div>
            </div>
          )}

          {teachContent.analogy && (
            <div className="learn-lesson-analogy">
              <strong>Think of it like:</strong> {teachContent.analogy}
            </div>
          )}

          {/* Audio Lesson Player */}
          {conceptId && nodes && topic && (
            <div className="learn-lesson-audio">
              <AudioLessonPlayer
                conceptId={conceptId}
                nodes={nodes}
                topic={topic}
                paperTitle={paperTitle}
                cachedAudio={audioCache?.[conceptId]}
                onCacheAudio={onCacheAudio}
              />
            </div>
          )}

          {/* Visual Mnemonic — Veo 3 video generation */}
          {onGenerateVideo && conceptId && (
            <div className="learn-lesson-mnemonic">
              {mnemonicJob?.status === 'complete' && mnemonicJob.videoUrl ? (
                <div className="learn-mnemonic-player">
                  <div className="learn-mnemonic-header">
                    <span className="learn-mnemonic-icon">▶</span>
                    <strong>Visual Mnemonic</strong>
                    {mnemonicJob.briefDescription && (
                      <span className="learn-mnemonic-brief">{mnemonicJob.briefDescription}</span>
                    )}
                  </div>
                  <video
                    src={mnemonicJob.videoUrl}
                    controls
                    autoPlay
                    loop
                    playsInline
                    className="learn-mnemonic-video"
                  />
                </div>
              ) : mnemonicJob?.status === 'generating' || mnemonicJob?.status === 'polling' ? (
                <div className="learn-mnemonic-generating">
                  <span className="learn-mnemonic-pulse">●</span>
                  <span>Generating visual mnemonic...</span>
                  {mnemonicJob.mnemonicStrategy && (
                    <span className="learn-mnemonic-strategy">{mnemonicJob.mnemonicStrategy}</span>
                  )}
                </div>
              ) : (
                <button
                  className="learn-mnemonic-btn"
                  onClick={() => onGenerateVideo(conceptId, { example: teachContent?.example, analogy: teachContent?.analogy })}
                  title="Generate a visual demonstration of this example"
                >
                  🎬 Visualize This Example
                </button>
              )}
            </div>
          )}

          <div className="learn-card-actions">
            <button className="learn-card-btn learn-card-btn-primary"
              onClick={() => onAction?.({ actionType: 'learnContinue' })}>
              Ready for Quiz →
            </button>
          </div>
        </div>
      )}

      {/* Adapt summary (inline alternative explanation) */}
      {isAdaptSummary && adaptSummary && (
        <div className="learn-card-lesson">
          <div className="learn-lesson-explanation">{adaptSummary}</div>
          <div className="learn-card-actions">
            <button className="learn-card-btn learn-card-btn-primary"
              onClick={() => onAction?.({ actionType: 'learnContinue' })}>
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Review Lesson (collapsible, available during probe and feedback) */}
      {_showingReview && cachedLesson && (
        <div className="learn-card-lesson learn-card-review">
          <div className="learn-lesson-explanation">{cachedLesson.explanation}</div>
          {cachedLesson.keyTakeaways?.length > 0 && (
            <div className="learn-lesson-takeaways">
              <strong>Key Takeaways:</strong>
              <ul>{cachedLesson.keyTakeaways.map((t, i) => <li key={i}>{t}</li>)}</ul>
            </div>
          )}
          {cachedLesson.example && (
            <div className="learn-lesson-example">
              <strong>Example:</strong>
              <div>{cachedLesson.example}</div>
            </div>
          )}
          {cachedLesson.analogy && (
            <div className="learn-lesson-analogy">
              <strong>Think of it like:</strong> {cachedLesson.analogy}
            </div>
          )}
        </div>
      )}

      {/* Concept context */}
      {(isProbing || isSocratic) && conceptReasoning && (
        <div className="learn-card-context">
          {conceptReasoning}
        </div>
      )}

      {/* Probe question — MCQ variant (lock-on-tap for single-select,
          tap-to-toggle + submit for multi-select) */}
      {isProbing && probe && probe._isMcq && (
        <McqProbe
          probe={probe}
          conceptId={conceptId}
          cachedLesson={cachedLesson}
          showingReview={_showingReview}
          onSubmit={(selectedOptionIds) => {
            onAction?.({
              actionType: 'submitLearnAnswer',
              answer: { mcq: true, selectedOptionIds },
            });
          }}
          onSkip={() => onAction?.({ actionType: 'learnSkip', conceptId })}
          onReviewLesson={() => onAction?.({ actionType: 'learnReviewLesson', conceptId })}
        />
      )}

      {/* Probe question — free-text variant (default) */}
      {isProbing && probe && !probe._isMcq && (
        <div className="learn-card-probe">
          <div className="learn-probe-question">
            <span className="learn-probe-type">{probe.probeType?.toUpperCase() || 'PROBE'}</span>
            {probe.question}
          </div>

          {/* Hints */}
          {showHint > 0 && probe.hints?.length > 0 && (
            <div className="learn-probe-hints">
              {probe.hints.slice(0, showHint).map((hint, i) => (
                <div key={i} className="learn-probe-hint">Hint {i + 1}: {hint}</div>
              ))}
            </div>
          )}

          <textarea
            className="learn-answer-input"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Type your answer here..."
            rows={3}
          />
          <div className="learn-card-actions">
            <button
              className="learn-card-btn learn-card-btn-primary"
              disabled={!answer.trim()}
              onClick={() => {
                onAction?.({ actionType: 'submitLearnAnswer', answer: answer.trim() });
                setAnswer('');
                setHintIndex(0);
              }}
            >
              Submit Answer
            </button>
            {probe.hints?.length > 0 && (showHint || 0) < probe.hints.length && (
              <button className="learn-card-btn" onClick={() => onAction?.({ actionType: 'learnHint' })}>
                Hint
              </button>
            )}
            <button className="learn-card-btn learn-card-btn-skip" onClick={() => onAction?.({ actionType: 'learnSkip', conceptId })}>
              Skip
            </button>
            {cachedLesson && (
              <button className="learn-card-btn" onClick={() => onAction?.({ actionType: 'learnReviewLesson', conceptId })}>
                {_showingReview ? 'Hide Lesson' : '📖 Review Lesson'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Socratic challenge */}
      {isSocratic && (
        <div className="learn-card-probe">
          <div className="learn-probe-question">
            <span className="learn-probe-type">MILESTONE CHALLENGE</span>
            {challenge}
          </div>

          {coveredConcepts?.length > 0 && (
            <div className="learn-card-covered">
              Covers: {coveredConcepts.map(c => c.label).join(', ')}
            </div>
          )}

          <textarea
            className="learn-answer-input"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Type your answer here..."
            rows={4}
          />
          <div className="learn-card-actions">
            <button
              className="learn-card-btn learn-card-btn-primary"
              disabled={!answer.trim()}
              onClick={() => {
                onAction?.({ actionType: 'submitLearnAnswer', answer: answer.trim() });
                setAnswer('');
              }}
            >
              Submit
            </button>
          </div>
        </div>
      )}

      {/* Feedback */}
      {isFeedback && (
        <div className="learn-card-feedback">
          <MasteryBadge mastery={mastery || 0} />
          {feedback && <div className="learn-feedback-text">{feedback}</div>}

          {correctAnswer && mastery < 8 && (
            <div className="learn-feedback-correct">
              <strong>The key insight:</strong> {correctAnswer}
            </div>
          )}

          {misconceptions?.length > 0 && (
            <div className="learn-feedback-misconceptions">
              <strong>Misconceptions detected:</strong>
              <ul>{misconceptions.map((m, i) => <li key={i}>{m}</li>)}</ul>
            </div>
          )}

          <div className="learn-card-actions">
            <button className="learn-card-btn learn-card-btn-primary" onClick={() => onAction?.({ actionType: 'learnContinue' })}>
              {nextAction === 'advance' ? 'Next Concept' : 'Continue'}
            </button>
            {nextAction !== 'advance' && (
              <button className="learn-card-btn" onClick={() => onAction?.({ actionType: 'learnExplainDifferently', conceptId })}>
                Explain Differently
              </button>
            )}
            {cachedLesson && (
              <button className="learn-card-btn" onClick={() => onAction?.({ actionType: 'learnReviewLesson', conceptId })}>
                {_showingReview ? 'Hide Lesson' : '📖 Review Lesson'}
              </button>
            )}
            {canDrill && (
              <button className="learn-card-btn" onClick={() => onAction?.({ actionType: 'learnExploreDeeper', conceptId, conceptLabel })}>
                ⧫ Explore Deeper
              </button>
            )}
          </div>
        </div>
      )}

      {/* Complete */}
      {isDone && finalMasteryMap && (
        <div className="learn-card-complete">
          <div className="learn-complete-summary">
            {Object.entries(finalMasteryMap).map(([id, data]) => (
              <div key={id} className="learn-complete-row">
                <span className="learn-complete-label">{data.label || id}</span>
                <MasteryBadge mastery={data.score || 0} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {isError && <div className="learn-card-error-msg">{error}</div>}
    </div>
  );
}
